// src/Admin/GestionCertificados.js — Solponiente
// Gestión de las solicitudes de certificado: generar borrador -> firmar con
// AutoFirma -> entregar al usuario. Ciclo de vida propio, separado del de
// las solicitudes estándar (no pasa por "en proceso" ni genera Expediente).
import React, { useEffect, useState } from "react";
import { Table, Button, Badge, InputGroup, Form, Spinner } from "react-bootstrap";
import FirmaCertificado from "../FirmaCertificado";

const BASE_URL = "https://oficinavirtual-solponiente.com/api";

// Mapa de tipos de certificado disponibles. Añadir aquí una nueva entrada
// (y su función equivalente en el backend, ver CERTIFICADOS_DISPONIBLES en
// solicitudes/services.py) es lo único necesario para ofrecer más tipos en
// el futuro — no requiere cambios de esquema.
export const TIPOS_CERTIFICADO = {
  al_corriente_pago: "Certificado de estar al corriente de pago",
};

const GestionCertificados = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(null);
  const [descargando, setDescargando] = useState(null);
  const token = localStorage.getItem("token");

  const fetchSolicitudes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/solicitudes/completas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSolicitudes(data.filter((s) => s.TipoSolicitud === "certificado"));
      }
    } catch (e) {
      console.error("Error al obtener las solicitudes de certificado:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolicitudes();
  }, []);

  const generarBorrador = async (solicitudId) => {
    setGenerando(solicitudId);
    try {
      const res = await fetch(`${BASE_URL}/solicitudes/${solicitudId}/generar-certificado`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchSolicitudes();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Error al generar el borrador del certificado.");
      }
    } catch (e) {
      alert("No se pudo conectar con el servidor.");
    } finally {
      setGenerando(null);
    }
  };

  const descargar = async (documentoId, nombre) => {
    setDescargando(documentoId);
    try {
      const res = await fetch(`${BASE_URL}/documentos/${documentoId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = nombre || "certificado.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert("No se pudo descargar el documento.");
    } finally {
      setDescargando(null);
    }
  };

  const renderEstado = (sol) => {
    if (sol.FirmadoAdmin) return <Badge bg="success">Enviado</Badge>;
    if (sol.DocumentoID) return <Badge bg="warning" text="dark">Borrador pendiente de firma</Badge>;
    return <Badge bg="secondary">Solicitado</Badge>;
  };

  const solicitudesFiltradas = solicitudes.filter((s) => {
    const term = filtro.toLowerCase();
    return (
      s.NumeroRegistro?.toLowerCase().includes(term) ||
      s.Usuario?.Nombre?.toLowerCase().includes(term) ||
      s.Usuario?.Apellidos?.toLowerCase().includes(term) ||
      s.Usuario?.Num_Identificacion?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="mt-2">
      <InputGroup className="mb-3">
        <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
        <Form.Control
          placeholder="Buscar por número de registro, nombre o identificación…"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        {filtro && <Button variant="outline-secondary" onClick={() => setFiltro("")}>✕</Button>}
      </InputGroup>

      {loading ? (
        <p className="text-muted">Cargando solicitudes de certificado…</p>
      ) : solicitudesFiltradas.length === 0 ? (
        <p className="text-muted">No hay solicitudes de certificado.</p>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Nº Registro</th>
                <th>Usuario</th>
                <th>Identificación</th>
                <th>Tipo de certificado</th>
                <th>Fecha</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {solicitudesFiltradas.map((sol) => (
                <tr key={sol.SolicitudID}>
                  <td><code className="text-primary fw-bold">{sol.NumeroRegistro}</code></td>
                  <td>{sol.Usuario?.Nombre} {sol.Usuario?.Apellidos}</td>
                  <td>{sol.Usuario?.Num_Identificacion}</td>
                  <td><small>{TIPOS_CERTIFICADO[sol.TipoCertificado] || sol.TipoCertificado}</small></td>
                  <td>
                    <small>
                      {sol.FechaSolicitud
                        ? new Date(sol.FechaSolicitud).toLocaleDateString("es-ES")
                        : "—"}
                    </small>
                  </td>
                  <td>{renderEstado(sol)}</td>
                  <td>
                    <div className="d-flex flex-column gap-1" style={{ minWidth: 170 }}>
                      {!sol.DocumentoID && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => generarBorrador(sol.SolicitudID)}
                          disabled={generando === sol.SolicitudID}
                        >
                          {generando === sol.SolicitudID ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <><i className="bi bi-file-earmark-plus me-1"></i>Generar borrador</>
                          )}
                        </Button>
                      )}

                      {sol.DocumentoID && !sol.FirmadoAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => descargar(sol.DocumentoID, sol.NombreDocumento)}
                            disabled={descargando === sol.DocumentoID}
                          >
                            <i className="bi bi-download me-1"></i>Ver borrador
                          </Button>
                          <FirmaCertificado
                            documentoId={sol.DocumentoID}
                            solicitudId={sol.SolicitudID}
                            onFirmado={fetchSolicitudes}
                          />
                        </>
                      )}

                      {sol.FirmadoAdmin && (
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => descargar(sol.DocumentoID, sol.NombreDocumento)}
                          disabled={descargando === sol.DocumentoID}
                        >
                          <i className="bi bi-download me-1"></i>Descargar firmado
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default GestionCertificados;
